import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/ui/design-system/components/ui/collapsible';
import { FiBarChart2, FiChevronDown } from 'react-icons/fi';
import {
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    useSidebar,
} from '@/ui/design-system/components/ui/sidebar';
import { generatePath, useRouteMatch } from 'react-router-dom';
import Link from '../Link';
import SidebarRelatedCategories from './SidebarRelatedCategories';
import useCountryParam from 'utils/hooks/useCountryParam';

const trendsByCategoryPath =
    '/explore/:dataSource(tiktok|google|instagram)?/trends/:categoryId?';
const ingredientsPath =
    '/explore/:dataSource(tiktok|google)?/ingredients/trends/:categoryId?';
const hashtagsPath =
    '/explore/:dataSource(tiktok|instagram)/hashtags/trends/:categoryId?';
const relatedCategoriesPath =
    '/explore/related-categories/:dataSource(tiktok|google|instagram)?/trends/:categoryId?';
const relatedCategoriesSelectionPath = '/explore/related-categories-selection';

function SidebarTrendsMenu({ isOpen: controlledOpen, onToggle }) {
    const { mutateToWithCountryParam } = useCountryParam();
    const [
        matchesTrendsByCategory,
        matchesIngredients,
        matchesHashtags,
        matchesRelatedCategories,
        matchesRelatedCategoriesSelection,
    ] = [
        trendsByCategoryPath,
        ingredientsPath,
        hashtagsPath,
        relatedCategoriesPath,
        relatedCategoriesSelectionPath,
        // eslint-disable-next-line react-hooks/rules-of-hooks
    ].map((path) => useRouteMatch({ path }));

    const match =
        matchesTrendsByCategory ||
        matchesIngredients ||
        matchesHashtags ||
        matchesRelatedCategories ||
        matchesRelatedCategoriesSelection;

    const defaultOpen = !!match;
    const categoryId = match?.params?.categoryId;
    const dataSource = match?.params?.dataSource;

    const trendsByCategoryTo = mutateToWithCountryParam(
        generatePath(trendsByCategoryPath, {
            dataSource,
            categoryId,
        }),
    );

    const hashtagsTo = mutateToWithCountryParam(
        generatePath(hashtagsPath, {
            dataSource:
                ['tiktok', 'instagram'].find((ds) => ds === dataSource) ||
                'tiktok',
            categoryId,
        }),
    );

    const { state, setOpen } = useSidebar();
    const open = controlledOpen ?? defaultOpen;

    const handleTriggerClick = (e) => {
        if (state === 'collapsed') {
            e.preventDefault();
            onToggle?.(true);
            setOpen(true);
        }
    };

    return (
        <Collapsible
            open={open}
            onOpenChange={onToggle}
            className="group/collapsible"
        >
            <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                        tooltip="Trends"
                        onClick={handleTriggerClick}
                    >
                        <FiBarChart2 />
                        <span>Trends</span>
                        <FiChevronDown className="ml-auto !text-sidebar-foreground transition-transform group-data-[state=open]/collapsible:rotate-180" />
                    </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <SidebarMenuSub>
                        <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                                asChild
                                isActive={!!matchesTrendsByCategory}
                            >
                                <Link
                                    to={trendsByCategoryTo}
                                    mixpanelContext="Trends By Category Explorer"
                                >
                                    <span>By Category</span>
                                </Link>
                            </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                                asChild
                                isActive={!!matchesHashtags}
                            >
                                <Link
                                    to={hashtagsTo}
                                    mixpanelContext="Trends Hashtags Explorer"
                                >
                                    <span>Hashtags</span>
                                </Link>
                            </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarRelatedCategories />
                    </SidebarMenuSub>
                </CollapsibleContent>
            </SidebarMenuItem>
        </Collapsible>
    );
}

export default SidebarTrendsMenu;
